/**
 * Google Analytics (gtag.js) — professional integration for SPA
 *
 * Features:
 *  • Dynamic script injection (keeps index.html clean)
 *  • Automatic page-view tracking on every SPA navigation
 *    (patches history.pushState / replaceState + listens to popstate)
 *  • Custom event helper for future tracking needs
 */

// ─── Configuration ───────────────────────────────────────────────

const GA_MEASUREMENT_ID = 'G-N0CZ14GHQP';

// ─── TypeScript declarations ─────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// ─── Core helpers ────────────────────────────────────────────────

/**
 * Lightweight wrapper around `window.gtag` that is safe to call
 * before the script has loaded.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function gtag(..._args: unknown[]): void {
  // gtag.js expects dataLayer entries to be the `arguments` object, not a plain array.
  // Using `arguments` preserves that contract.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

// ─── Page-view tracking ─────────────────────────────────────────

/**
 * Send a page_view event to GA4.
 *
 * @param path  – defaults to the current `location.pathname + location.search`
 * @param title – defaults to `document.title`
 */
export function trackPageview(
  path?: string,
  title?: string,
): void {
  gtag('event', 'page_view', {
    page_path: path ?? window.location.pathname + window.location.search,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}

// ─── Custom event tracking ──────────────────────────────────────

/**
 * Track a custom event in GA4.
 *
 * Usage:
 *   trackEvent('cta_click', 'engagement', 'hero_banner');
 *   trackEvent('form_submit', 'lead', 'contact_form', 1);
 */
export function trackEvent(
  action: string,
  category?: string,
  label?: string,
  value?: number,
): void {
  gtag('event', action, {
    event_category: category,
    event_label: label,
    value,
  });
}

// ─── SPA history patching ───────────────────────────────────────

let historyPatched = false;

function patchHistory(): void {
  if (historyPatched) return;
  historyPatched = true;

  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  history.pushState = function pushState(
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ): void {
    originalPushState(data, unused, url);
    trackPageview();
  };

  history.replaceState = function replaceState(
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ): void {
    originalReplaceState(data, unused, url);
    trackPageview();
  };

  window.addEventListener('popstate', () => {
    trackPageview();
  });
}

// ─── Initialisation ─────────────────────────────────────────────

let initialised = false;

/**
 * Bootstrap Google Analytics.
 * Safe to call multiple times — only the first call has an effect.
 */
export function initGA(): void {
  if (initialised) return;
  initialised = true;

  // 1. Inject the gtag.js loader script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // 2. Initialise dataLayer & gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // we send page_views manually after each SPA navigation
  });

  // 3. Send the initial page view
  trackPageview();

  // 4. Patch History API for SPA page-view tracking
  patchHistory();
}
